import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import type { ReactNode } from "react";
import { createContext, useContext, useState } from "react";

export const sortTypeMap = {
  Latest: "latest",
  Oldest: "oldest",
  Popular: "popular",
  Likes: "likes",
  Alphabetical: "alphabetical",
  NewActivity: "new_activity",
  Relevance: "relevance",
} as const;

export type SortType = (typeof sortTypeMap)[keyof typeof sortTypeMap];

// Type guard to check if a value is a valid SortType
const isSortType = (value: any): value is SortType =>
  Object.values(sortTypeMap).includes(value);

interface SortContextType {
  setSort: (type: string, sort: SortType) => void;
  sort: Record<string, SortType | undefined>;
}

const SortContext = createContext<SortContextType>({
  sort: {
    posts: sortTypeMap.Relevance,
    comments: sortTypeMap.Relevance,
    events: sortTypeMap.Relevance,
    lessons: sortTypeMap.Relevance,
    members: sortTypeMap.Relevance,
  },
  setSort: () => {},
});

SortContext.displayName = "SortContext";

export const SortContextProvider = ({ children }: { children: ReactNode }) => {
  const { currentCommunity } = usePunditUserContext();
  const communityDefaultSort = currentCommunity?.default_search_sorting;
  const defaultSortType =
    communityDefaultSort && isSortType(communityDefaultSort)
      ? communityDefaultSort
      : sortTypeMap.Relevance;

  const [sort, setSortState] = useState<SortContextType["sort"]>({
    posts: defaultSortType,
    comments: defaultSortType,
    events: defaultSortType,
    lessons: defaultSortType,
    members: defaultSortType,
  });

  const setSort = (type: string, newSort: SortType) => {
    setSortState(prev => ({
      ...prev,
      [type]: newSort,
    }));
  };

  return (
    <SortContext.Provider value={{ sort, setSort }}>
      {children}
    </SortContext.Provider>
  );
};

export const useSortContext = () => useContext(SortContext);
